<template>
  <div class="pot-wrapper">
    <!-- ═══════════════════════════════════════════
         PANEL: Bảng chi tiết khoản phụ (Principal Overrides)
         ═══════════════════════════════════════════ -->
    <div class="sk-panel mb-4">
      <div class="sk-panel-head">
        <div class="sk-panel-icon sk-panel-icon--warning"><i class="el-icon-s-operation" /></div>
        <div>
          <p class="sk-panel-title">Chi tiết khoản phụ</p>
          <p class="sk-panel-subtitle">
            Các khoản phụ trong công thức. Giá riêng khác giá gốc sẽ được đánh dấu.
          </p>
        </div>
      </div>

      <!-- Công thức -->
      <div v-if="formulaExpression" class="pot-formula-row mb-3">
        <div class="pot-formula-header">
          <i class="el-icon-cpu pot-formula-icon" />
          <span class="pot-formula-label">Công thức tính toán:</span>
        </div>
        <div class="pot-formula-body">
          <span class="pot-formula-value">{{ formulaExpression }}</span>
          <span v-if="computedFormulaPreview" class="pot-formula-preview">
            = {{ computedFormulaPreview }}
          </span>
        </div>
      </div>

      <!-- Bảng -->
      <el-table
        :data="overrides"
        border
        class="sk-table pot-table"
        :empty-text="emptyText"
        v-loading="loading"
      >
        <el-table-column label="Biến" align="center" width="65">
          <template slot-scope="{ row }">
            <span class="pot-alias">{{ row.alias }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Tên khoản phụ" min-width="160">
          <template slot-scope="{ row }">
            <span class="pot-name">{{ row.principalName }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Giá gốc" align="right" width="135">
          <template slot-scope="{ row }">
            <span class="pot-money">{{ formatMoney(row.originalPrice) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Giá riêng HS" align="right" width="155">
          <template slot-scope="{ row }">
            <div class="pot-price-cell">
              <span :class="['pot-money', { 'pot-money--custom': Number(row.price) !== Number(row.originalPrice) }]">
                {{ formatMoney(row.price) }}
              </span>
              <el-tooltip v-if="Number(row.price) !== Number(row.originalPrice)" content="Giá đã bị ghi đè riêng cho học sinh này" placement="top">
                <i class="el-icon-edit-outline pot-warn-icon" />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="SL" align="center" width="60">
          <template slot-scope="{ row }">
            <span :class="['pot-qty', { 'pot-qty--custom': Number(row.quantity) !== Number(row.originalQuantity) }]">
              {{ formatQuantity(row.quantity) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Đơn vị" align="center" width="85">
          <template slot-scope="{ row }">
            <span :class="['pot-unit', { 'pot-unit--custom': row.unit !== row.originalUnit }]">
              {{ row.unit || '—' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Giảm giá" align="center" width="105">
          <template slot-scope="{ row }">
            <template v-if="row.discount">
              <span class="pot-discount-tag">
                <span v-if="row.discountType === 'PERCENT'">-{{ row.discountNumber }}%</span>
                <span v-else>-{{ formatMoney(row.discountNumber) }}</span>
              </span>
            </template>
            <span v-else class="pot-text-muted">—</span>
          </template>
        </el-table-column>

        <el-table-column label="Trạng thái" align="center" width="115">
          <template slot-scope="{ row }">
            <el-tag
              :type="isDefault(row) ? 'info' : 'warning'"
              size="mini"
              class="pot-status-tag"
            >
              {{ isDefault(row) ? 'Mặc định' : 'Đã ghi đè' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- Nút hành động -->
      <div class="pot-actions mt-3">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-edit"
          @click="$emit('edit')"
        >
          Sửa giá khoản phụ
        </el-button>
        <el-button
          v-if="hasCustomPrice"
          type="warning"
          size="small"
          icon="el-icon-refresh-left"
          :loading="resetting"
          @click="handleReset"
        >
          Reset về mặc định
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatQuantity as formatQuantityValue } from "@/components/tutition_v3/utils/quantity";

export default {
  name: "PrincipalOverrideTable",

  props: {
    /** Danh sách override từ API */
    overrides: {
      type: Array,
      default: () => [],
    },
    /** Biểu thức công thức (vd: "(A + B) * 0.9") */
    formulaExpression: {
      type: String,
      default: "",
    },
    /** Đang loading data */
    loading: {
      type: Boolean,
      default: false,
    },
    /** Đang reset */
    resetting: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    emptyText() {
      return "Không có dữ liệu khoản phụ";
    },

    /** Có ít nhất 1 khoản phụ khác giá gốc hoặc có giảm giá */
    hasCustomPrice() {
      return this.overrides.some(
        (item) => Number(item.price) !== Number(item.originalPrice) ||
                  Number(item.quantity) !== Number(item.originalQuantity) ||
                  item.unit !== item.originalUnit ||
                  item.discount === true
      );
    },

    /** Preview công thức với giá thực */
    computedFormulaPreview() {
      if (!this.formulaExpression || !this.overrides.length) return "";
      try {
        let expr = this.formulaExpression;
        this.overrides.forEach((item) => {
          if (!item.alias) return;
          // Tính giá sau giảm
          let effectivePrice = Number(item.price) * Number(item.quantity || 1);
          if (item.discount && item.discountNumber) {
            if (item.discountType === "PERCENT") {
              effectivePrice = effectivePrice * (1 - item.discountNumber / 100);
            } else if (item.discountType === "MONEY") {
              effectivePrice = Math.max(0, effectivePrice - item.discountNumber);
            }
          }
          // Replace alias with value (word boundary)
          const regex = new RegExp(`\\b${item.alias}\\b`, "g");
          expr = expr.replace(regex, effectivePrice.toString());
        });
        // Evaluate safely (only numbers and math operators)
        const sanitized = expr.replace(/[^0-9+\-*/().,%\s]/g, "");
        if (!sanitized) return "";
        // eslint-disable-next-line no-eval
        const result = eval(sanitized);
        if (isNaN(result) || !isFinite(result)) return "";
        return this.formatMoney(Math.round(result));
      } catch {
        return "";
      }
    },
  },

  methods: {
    /** Kiểm tra row có đang ở trạng thái mặc định */
    isDefault(row) {
      return Number(row.price) === Number(row.originalPrice) &&
             Number(row.quantity) === Number(row.originalQuantity) &&
             row.unit === row.originalUnit &&
             !row.discount;
    },

    /** Format tiền VNĐ */
    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("vi-VN") + " ₫";
    },

    formatQuantity(value) {
      return formatQuantityValue(value);
    },

    /** Emit reset event (parent sẽ gọi confirm) */
    handleReset() {
      this.$emit("reset");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-table.scss";

.pot-wrapper {
  /* No extra spacing – parent manages margins */
}

/* ─── Math/Formula Widget ─────────────────────────── */
.pot-formula-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%);
  border: 1px solid #dbeafe;
  border-left: 4px solid #4f46e5;
  border-radius: 8px;
  text-align: left;
}
.pot-formula-header {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pot-formula-icon {
  font-size: 16px;
  color: #4f46e5;
}
.pot-formula-label {
  font-size: 13px;
  font-weight: 700;
  color: #3730a3;
}
.pot-formula-body {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pot-formula-value {
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 13px;
  color: #1e1b4b;
  font-weight: 600;
  background: #ffffff;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px dashed #cbd5e1;
}
.pot-formula-preview {
  font-size: 13px;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #a7f3d0;
}

/* ─── Table & Cells ───────────────────────────────── */
.pot-table {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/deep/ .pot-table thead th {
  background: #4f46e5 !important; /* Premium Indigo header */
  color: #ffffff !important;
  font-weight: 700;
  height: 44px;
}
/deep/ .pot-table thead th > .cell {
  color: #ffffff !important;
}

/deep/ .pot-table tbody tr.hover-row {
  & > td {
    background-color: #f5f3ff !important; /* Soft indigo hover background */
  }
}

.pot-alias {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e0f2fe;
  border: 1px solid #bae6fd;
  border-radius: 50%; /* Circle shape */
  font-weight: 700;
  font-size: 12px;
  color: #0369a1;
}

.pot-name {
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  display: block;
}

.pot-money {
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: #475569;
}

.pot-money--custom {
  color: #ea580c; /* Warm orange for custom price overrides */
  font-weight: 700;
  background-color: #fff7ed;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #ffedd5;
}

.pot-price-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.pot-warn-icon {
  color: #f97316;
  font-size: 14px;
  cursor: pointer;
}

.pot-qty {
  font-size: 13px;
  color: #475569;
}
.pot-qty--custom {
  color: #ea580c;
  font-weight: 700;
}

.pot-unit {
  font-size: 12.5px;
  color: #64748b;
}
.pot-unit--custom {
  color: #ea580c;
  font-weight: 700;
}

/* Discount Badge */
.pot-discount-tag {
  display: inline-block;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-weight: 700;
  font-size: 11.5px;
  padding: 2px 8px;
  border-radius: 6px;
}

/* Status tags */
/deep/ .pot-status-tag {
  border-radius: 6px !important;
  font-weight: 600;
  border-width: 1px;
}
/deep/ .pot-status-tag.el-tag--info {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
  color: #475569 !important;
}
/deep/ .pot-status-tag.el-tag--warning {
  background-color: #fff7ed !important;
  border-color: #fcd34d !important;
  color: #c2410c !important;
}

/* Actions */
.pot-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 18px;
  
  /deep/ .el-button {
    border-radius: 8px;
    font-weight: 600;
    padding: 9px 16px;
    transition: all 0.2s ease;
  }

  /deep/ .el-button--primary {
    background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%) !important;
    border: none !important;
    box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.2) !important;
    color: #ffffff !important;
    
    &:hover {
      box-shadow: 0 4px 12px -1px rgba(79, 70, 229, 0.3) !important;
      transform: translateY(-1px);
    }
    &:active {
      transform: translateY(0);
    }
  }
  
  /deep/ .el-button--warning {
    background: #ffffff !important;
    border: 1px solid #ea580c !important;
    color: #ea580c !important;
    box-shadow: none !important;
    
    &:hover {
      background: #fff7ed !important;
      color: #c2410c !important;
      border-color: #c2410c !important;
      transform: translateY(-1px);
    }
    &:active {
      transform: translateY(0);
    }
  }
}
</style>
